import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, Input, Modal, Table } from 'antd';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { captureException } from 'utils/errors';
import { getData } from 'utils';
import { deleteSegment, getSegmentStatistics, getSegments } from 'api/segments';
import CreateSegmentModal from './CreateSegmentModal';
import { formatConditionForDisplay } from './constants';
import { StyledWrapper } from './styles';

function Segments() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [stats, setStats] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const navigate = useNavigate();

  const pageSize = 10;

  const loadSegments = useCallback(() => {
    setLoading(true);
    getSegments({ search, page, page_size: pageSize })
      .then((res) => {
        const data = getData(res)?.data || getData(res);
        const results = data?.results ?? [];
        setSegments(Array.isArray(results) ? results : []);
        setPagination({
          count: data?.count ?? 0,
          next: data?.next,
          previous: data?.previous,
        });
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);
      });
  }, [search, page]);

  const loadStats = useCallback(() => {
    getSegmentStatistics()
      .then((res) => {
        const data = getData(res)?.data || getData(res);
        setStats(data);
      })
      .catch(() => setStats(null));
  }, []);

  const debouncedSearch = useDebouncedCallback((value) => {
    setSearch(value);
    setPage(1);
  }, 500);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  useEffect(() => {
    if (segments.length > 0 || page === 1) {
      loadStats();
    }
  }, [segments.length, page, loadStats]);

  const handleCreateSuccess = (createdSegment, customerCount) => {
    setSuccessModalData({ segment: createdSegment, customerCount });
    loadSegments();
    loadStats();
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setSuccessModalData(null);
  };

  const handleCloseSuccess = () => {
    setSuccessModalData(null);
    setCreateModalOpen(true); // Re-open for "Create New"
  };

  const handleViewSegment = (segmentId) => {
    navigate(`/console/segments/${segmentId}`);
  };

  const handleEditSegment = (seg) => {
    setEditingSegment(seg);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setEditingSegment(null);
    loadSegments();
    loadStats();
  };

  const handleDeleteSegment = (seg) => {
    Modal.confirm({
      title: 'Delete segment',
      content: `Are you sure you want to delete "${seg.name}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () =>
        deleteSegment(seg.id)
          .then(() => {
            loadSegments();
            loadStats();
          })
          .catch(captureException),
    });
  };

  const formatConditionsForExpand = (conditions) => {
    if (!conditions || !Array.isArray(conditions)) return null;
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {conditions.map((c, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {formatConditionForDisplay(c)}
          </li>
        ))}
      </ul>
    );
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleViewSegment(row.id);
          }}
        >
          {name}
        </a>
      ),
    },
    {
      title: 'Customers',
      dataIndex: 'customer_count',
      key: 'customer_count',
      render: (val) => val ?? 0,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, row) => (
        <>
          <Button type="link" size="small" onClick={() => handleViewSegment(row.id)}>
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSegment(row)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteSegment(row)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ];

  const isEmpty = !loading && segments.length === 0;
  const searchHasNoResults = isEmpty && search.length > 0;
  const trulyEmpty = isEmpty && !search;

  return (
    <Layout>
      <StyledWrapper>
        <div className="segments-header">
          <div>
            <h1 className="segments-title">Segments</h1>
            <p className="segments-subtitle">
              Group customers based on spend, behavior, and ordering patterns.
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            New Segment
          </Button>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-card-label">Total Segments</div>
            <div className="summary-card-value">{stats?.total_segments ?? 0}</div>
          </div>
          <div className="summary-card">
            <div className="summary-card-label">Customers in Segments</div>
            <div className="summary-card-value">
              {stats?.customers_in_segments?.toLocaleString() ?? 0}
            </div>
          </div>
        </div>

        <div className="search-bar">
          <Input
            placeholder="Search segments by name..."
            onChange={(e) => debouncedSearch(e.target.value)}
            allowClear
          />
          <SearchOutlined style={{ color: 'white', fontSize: 18 }} />
        </div>

        {trulyEmpty ? (
          <Card>
            <div className="empty-state">
              <div className="empty-illustration">
                <SearchOutlined style={{ fontSize: 120 }} />
              </div>
              <div className="empty-title">No segments yet</div>
              <div className="empty-desc">
                Create your first segment to group customers based on spend, behavior, or
                ordering patterns.
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalOpen(true)}
              >
                New Segment
              </Button>
            </div>
          </Card>
        ) : searchHasNoResults ? (
          <Card>
            <div className="empty-state">
              <div className="empty-title">No results match your search</div>
              <div className="empty-desc">
                Try a different search term or clear the search to see all segments.
              </div>
            </div>
          </Card>
        ) : (
          <Table
            loading={loading}
            columns={columns}
            dataSource={segments}
            rowKey="id"
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '8px 0' }}>
                  <div style={{ fontSize: 12, color: '#717679', marginBottom: 8 }}>Conditions</div>
                  {record.conditions?.length > 0 ? (
                    formatConditionsForExpand(record.conditions)
                  ) : (
                    <span style={{ color: '#717679' }}>No conditions</span>
                  )}
                </div>
              ),
              rowExpandable: () => true,
            }}
            pagination={{
              current: page,
              pageSize,
              total: pagination.count,
              showSizeChanger: false,
              onChange: setPage,
            }}
          />
        )}

        <CreateSegmentModal
          open={createModalOpen || !!successModalData}
          onClose={handleCloseCreateModal}
          onCloseSuccess={handleCloseSuccess}
          onSuccess={handleCreateSuccess}
          successData={successModalData}
          onViewSegment={handleViewSegment}
        />
        <CreateSegmentModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingSegment(null);
          }}
          onSuccess={handleEditSuccess}
          segment={editingSegment}
          mode="edit"
        />
      </StyledWrapper>
    </Layout>
  );
}

export default withGuard(Segments, [PERMISSIONS.MARKETING], { redirect: true });
